import tw, { styled, css } from 'twin.macro';

export const StyledButton = styled.button.attrs((props) => ({
  className: props.className
}))(({ disabled, $color, $size, $variant, $contentType }) => [
  tw`border-0 m-0 p-0 cursor-pointer relative flex items-center justify-center align-middle no-underline bg-transparent rounded-none select-none appearance-none`,
  tw`mb-0 no-underline`,
  tw`border-0`,

  /* Small Button */
  $size === 'small' && tw`p-4 px-8 leading-20 font-medium text-sm`,
  $size === 'small' &&
    $variant === 'outlined' &&
    css`
      padding: 0.1875rem 0.4375rem; /* 3px 7px, padding minus the border to have a total height of 28px for the small outlined button */
    `,

  /* Medium Button */

  $size === 'medium' &&
    css`
      padding-top: 0.375rem;
      padding-bottom: 0.375rem;
      ${tw`px-16 leading-normal font-medium text-lg`}
    `,
  $size === 'medium' &&
    $variant === 'outlined' &&
    css`
      padding: 0.3125rem 0.9375rem; /* 5px 15px, padding minus the border to have a total height of 36px for the medium outlined button */
    `,

  /* Large Button */

  $size === 'large' && tw`py-8 px-24 leading-28 font-medium text-xl`,
  $size === 'large' &&
    $variant === 'outlined' &&
    css`
      padding: 0.4375rem 1.4375rem; /* 7px 23px, padding minus the border to have a total height of 44px for the large outlined button */
    `,

  /* Toggle Button */
  // guarantee that the contentType toggle Button has the same height as the medium size Button
  $contentType === 'toggle' &&
    css`
      padding-top: calc(6 / 16 * 1rem); // 6px to rem
      padding-bottom: calc(6 / 16 * 1rem); // 6px to rem
    `,

  /* Default classes after typography */
  tw`rounded whitespace-nowrap transition min-w-0`,

  /* State */
  tw`opacity-100 transition-opacity duration-100 active:opacity-50 disabled:(cursor-not-allowed opacity-50 bg-transparent)`,

  /* Color */
  $color === 'primary'
    ? tw`text-blue-base text-opacity-95 bg-transparent can-hover:hover:bg-blue-lighten-30 active:bg-blue-lighten-30 focus-visible:bg-blue-lighten-30 disabled:(text-blue-base text-opacity-95 bg-transparent)`
    : tw`text-black text-opacity-60 bg-transparent can-hover:hover:(text-black text-opacity-85 bg-silver-lighten-15) active:(text-black text-opacity-85 bg-silver-lighten-15) focus-visible:(text-black text-opacity-85 bg-silver-lighten-15) disabled:(text-black text-opacity-60 bg-transparent)`,
  $color === 'default' && $variant === 'outlined' && tw`border-silver-darken-10`,
  $color === 'primary' && $variant === 'outlined' && tw`border-blue-base`,
  $color === 'primary' &&
    $variant === 'filled' &&
    tw`bg-blue-base text-white can-hover:hover:(bg-blue-darken-10 text-white) active:(bg-blue-darken-10 text-white) focus-visible:(bg-blue-darken-10 text-white) disabled:(text-white bg-blue-darken-10)`,

  /* Variant */
  $variant === 'outlined' && tw`border border-solid`,
  $color === 'default' &&
    $variant === 'filled' &&
    tw`bg-silver-base text-opacity-85 can-hover:hover:bg-silver-darken-10 active:bg-silver-darken-10 focus-visible:bg-silver-darken-10 disabled:(bg-silver-darken-10)`
]);

export const StyledIconButton = styled.button.attrs((props) => ({
  className: props.className,
  disabled: props.disabled
}))(({ $color, $variant, $size }) => [
  tw`border-0 m-0 p-0 cursor-pointer relative flex items-center justify-center align-middle no-underline bg-transparent select-none appearance-none`,
  tw`flex-grow-0 flex-shrink-0 p-4 overflow-visible text-xl text-center rounded-full opacity-100 transition-opacity duration-100 active:opacity-50 disabled:( cursor-not-allowed opacity-50 bg-transparent)`,

  /* color property */
  $color === 'default' &&
    tw`text-black text-opacity-60 bg-transparent can-hover:hover:(text-black text-opacity-85 bg-silver-lighten-15 ) active:(text-black text-opacity-85 bg-silver-lighten-15) focus-visible:(text-black text-opacity-85 bg-silver-lighten-15) disabled:(text-opacity-60 bg-transparent)`,
  $color === 'primary' &&
    tw`text-blue-base text-opacity-95 bg-transparent can-hover:hover:bg-blue-lighten-30 active:bg-blue-lighten-30 focus-visible:bg-blue-lighten-30 disabled:(bg-transparent text-opacity-95 )`,
  $color === 'default' &&
    $variant === 'filled' &&
    tw`bg-silver-base text-opacity-85 can-hover:hover:bg-silver-darken-10 active:bg-silver-darken-10 focus-visible:bg-silver-darken-10 disabled:(bg-silver-darken-10)`,
  $color === 'primary' &&
    $variant === 'filled' &&
    tw`bg-blue-base text-white can-hover:hover:(bg-blue-darken-10 text-white) active:(bg-blue-darken-10 text-white) focus-visible:(bg-blue-darken-10 text-white) disabled:(bg-blue-darken-10)`,

  $size === 'medium' && `padding: 0.375rem`,
  $size === 'large' && `padding: 0.5rem`
]);

export const StyledButtonIconSpan = styled.span(({ $position, $size, $contentType }) => [
  tw`z-1 inline-flex text-current can-hover:hover:text-current focus-visible:text-current active:text-current`,
  $position === 'none' && '',
  $position === 'right' && $size === 'small' && tw`ml-4 -mr-4`,
  $position === 'right' &&
    $size === 'medium' &&
    css`
      margin-right: -0.375rem;
      ${tw`ml-4`}
    `,
  $position === 'right' && $size === 'large' && tw`ml-4 -mr-8`,
  $position === 'left' && $size === 'small' && tw`mr-4 -ml-4`,
  $position === 'left' &&
    $size === 'medium' &&
    css`
      margin-left: -0.375rem;
      ${tw`mr-4`};
    `,
  $position === 'left' && $size === 'large' && tw`mr-4 -ml-8`,

  /* Define icon size for button contentType icon */
  $contentType === 'icon' &&
    $size === 'small' &&
    `
        font-size: 1.25rem;
    `,
  $contentType === 'icon' &&
    $size === 'medium' &&
    `
        font-size: 1.5rem;
    `,
  $contentType === 'icon' &&
    $size === 'large' &&
    `
          font-size: 1.75rem;
      `
]);

export const StyledButtonTextSpan = styled.span.attrs(() => ({
  'data-content-type': 'buttonTextSpan'
}))(() => [tw`w-full flex items-center justify-center`]);
